import React, { useCallback, type ReactNode, type Ref } from 'react';
import { styled } from '@compiled/react';
import Avatar from '@atlaskit/avatar';
import { Anchor, Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type { ProjectOption } from '../../common/types.tsx';

export type Props = {
	value: ProjectOption | null;
	noValueText?: string;
	onClick?: (e: MouseEvent | KeyboardEvent) => void;
	/**
	 * When `true` the project link text will cut off and show ellipsis if it exceeds the available space.
	 * When `false` the project link text will break onto the next line.
	 * Default value is `true`
	 */
	isTruncated?: boolean;
	contentRef?: Ref<HTMLAnchorElement>;
};

export const ProjectReadView = ({
	value,
	noValueText,
	onClick,
	isTruncated = true,
	contentRef,
}: Props): React.JSX.Element => {
	const onClickLink = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
		(e) => {
			e.stopPropagation();
			onClick && onClick(e);
		},
		[onClick],
	);

	if (value === undefined || value === null) {
		return (
			<Box xcss={emptyValueContainerStyles}>
				<EmptyValue data-testid="issue-field-project.ui.view-read-view-empty-value">
					{noValueText || ''}
				</EmptyValue>
			</Box>
		);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const stopPropagation = (e: any) => {
		e.stopPropagation();
	};

	return (
		<ReadViewContainer data-component-selector="project-read-view-container">
			{Boolean(value.avatarUrls['48x48']) && (
				<AvatarWrapper>
					<Avatar
						appearance="square"
						size="xsmall"
						src={value.avatarUrls['48x48']}
						borderColor="transparent"
						testId="issue-field-project.ui.project-avatar"
					/>
				</AvatarWrapper>
			)}
			<Box xcss={labelContainerStyles}>
				<Anchor
					testId="issue-field-project.ui.view-label-link"
					onClick={onClickLink}
					onKeyUp={stopPropagation}
					onKeyDown={stopPropagation}
					href={`/projects/${value.key}`}
					xcss={[
						labelLinkStylesBase,
						isTruncated ? labelLinkStylesTruncated : labelLinkStylesNonTruncated,
					]}
					ref={contentRef}
				>
					{value.name}
				</Anchor>
			</Box>
		</ReadViewContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmptyValue = styled.span({
	color: token('color.text.subtlest'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReadViewContainer = styled.div({
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	paddingTop: token('space.050'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.050'),
	paddingLeft: token('space.050'),
});

const emptyValueContainerStyles = xcss({
	paddingLeft: 'space.075',
	paddingRight: 'space.075',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AvatarWrapper = styled.div<{
	children?: ReactNode;
}>({
	marginRight: token('space.100'),
	display: 'flex',
});

const labelContainerStyles = xcss({
	display: 'inline-flex',
	minWidth: '0',
	marginRight: 'space.100',
});

const labelLinkStylesBase = xcss({
	display: 'inline-block',
	maxWidth: '100%',
	textDecoration: 'none',
	':hover': {
		textDecoration: 'underline',
	},
});

const labelLinkStylesTruncated = xcss({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const labelLinkStylesNonTruncated = xcss({ overflowWrap: 'anywhere' });
